import { createAction } from '@ngrx/store';
import { SupportRequestSortColumn, SupportRequestStatusFilter } from '../enums';
import { AssignSupportModel, SupportRequest, SupportRequestsDashboardData, SupportRequestsDashboardUpdateData, SupportRequestsFilterOptions } from '../models';

/**
 * Enum for support action types.
 */
export enum SupportActionTypes {
  SUPPORT_INIT_SUCCESS = '[SUPPORT] INIT SUCCESS',
  SUPPORT_REQUEST_LOAD_INIT = '[SUPPORT] SUPPORT REQUEST LOAD INIT',
  SUPPORT_REQUESTS_LOAD = '[SUPPORT] SUPPORT REQUESTS LOAD',
  SUPPORT_QUEUES_LOAD_SUCCESS = '[SUPPORT] SUPPORT QUEUES LOAD SUCCESS',
  SUPPORT_QUEUES_LOAD_FAIL = '[SUPPORT] SUPPORT QUEUES LOAD FAIL',
  SUPPORT_REQUESTS_REFRESH = '[SUPPORT] SUPPORT REQUESTS REFRESH',
  SUPPORT_REQUESTS_UNSUBSCRIBE = '[SUPPORT] SUPPORT REQUESTS UNSUBSCRIBE',
  SUPPORT_REQUEST_CHANGED = '[SUPPORT] SUPPORT REQUEST CHANGED',
  SUPPORT_REQUEST_DASHBOARD_CHANGED = '[SUPPORT] SUPPORT REQUEST DASHBOARD CHANGED',
  SUPPORT_REQUEST_SHOW_ASSIGNEES_SEARCH = '[SUPPORT] SUPPORT REQUEST SHOW ASSIGNEES SEARCH',
  SUPPORT_REQUEST_ACCEPT = '[SUPPORT] SUPPORT REQUEST ACCEPT',
  SUPPORT_REQUEST_ACCEPT_SUCCESS = '[SUPPORT] SUPPORT REQUEST ACCEPT SUCCESS',
  SUPPORT_REQUEST_RESOLVE = '[SUPPORT] SUPPORT REQUEST RESOLVE',
  SUPPORT_REQUEST_RESOLVE_SUCCESS = '[SUPPORT] SUPPORT REQUEST RESOLVE SUCCESS',
  SUPPORT_REQUEST_ASSIGN = '[SUPPORT] SUPPORT REQUEST ASSIGN',
  SUPPORT_REQUEST_ASSIGN_SUCCESS = '[SUPPORT] SUPPORT REQUEST ASSIGN SUCCESS',
  SUPPORT_REQUEST_ASSIGN_RECEIVED = '[SUPPORT] SUPPORT REQUEST ASSIGN RECEIVED',
  SUPPORT_REQUEST_ASSIGNED_LOAD_SUCCESS = '[SUPPORT] SUPPORT REQUEST ASSIGNED LOAD SUCCESS',
  SUPPORT_REQUEST_USER_ID_LOAD_SUCCESS = '[SUPPORT] SUPPORT REQUEST USER ID LOADED SUCCESS',
  SUPPORT_REQUEST_SHOW_NOTIFICATION = '[SUPPORT] SUPPORT REQUEST SHOW NOTIFICATION',
  SUPPORT_REQUEST_DISMISS_NOTIFICATION = '[SUPPORT] SUPPORT REQUEST DISMISS NOTIFICATION',
  SUPPORT_REQUEST_STOP_RINGING = '[SUPPORT] SUPPORT REQUEST STOP RINGING',
  SUPPORT_REQUEST_FILTER_CHANGE = '[SUPPORT] SUPPORT REQUEST FILTER CHANGE',
  SUPPORT_REQUEST_CHART_TOGGLE_CHANGE = '[SUPPORT] SUPPORT REQUEST CHART TOGGLE CHANGE',
}

/**
 * Actions for the support feature.
 */
export class SupportActions {
  /**
   * Notify that the support feature has been initialized.
   */
  static supportInitSuccessAction = createAction(SupportActionTypes.SUPPORT_INIT_SUCCESS);

  /**
   * Initialize the support feature.
   */
  static supportRequestLoadInitAction = createAction(SupportActionTypes.SUPPORT_REQUEST_LOAD_INIT);

  /**
   * Load support requests.
   */
  static supportRequestLoadAction = createAction(
    SupportActionTypes.SUPPORT_REQUESTS_LOAD,
    (
      sortByDesc: boolean,
      sortBy: SupportRequestSortColumn,
      status: SupportRequestStatusFilter,
      skip: number,
      take: number,
      includeCharts: boolean
    ) => ({
      sortByDesc,
      sortBy,
      status,
      skip,
      take,
      includeCharts,
    })
  );

  /**
   * Notify that support requests is failed to load data.
   */
  static supportRequestLoadFailAction = createAction(SupportActionTypes.SUPPORT_QUEUES_LOAD_FAIL);

  /**
   * Notify that support requests have been loaded successfully.
   * @param supportRequests The support requests.
   */
  static supportRequestLoadSuccessAction = createAction(
    SupportActionTypes.SUPPORT_QUEUES_LOAD_SUCCESS,
    (supportRequests: SupportRequestsDashboardData) => ({ supportRequests })
  );

  /**
   * Refresh support requests.
   * @param supportRequests The support requests.
   */
  static supportRequestRefreshAction = createAction(SupportActionTypes.SUPPORT_REQUESTS_REFRESH);

  /**
   * Unsubscribe from support request updates.
   */
  static supportRequestUnsubscribeAction = createAction(SupportActionTypes.SUPPORT_REQUESTS_UNSUBSCRIBE);

  /**
   * Notify that support request changed
   */
  static supportRequestChangedAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_CHANGED,
    (supportRequest: SupportRequest) => ({ supportRequest })
  );

  /**
   * Notify that support request dashboard changed
   */
  static supportRequestDashboardChangedAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_DASHBOARD_CHANGED,
    (dashboardData: SupportRequestsDashboardUpdateData) => ({ dashboardData })
  );

  /**
   * Support request search assignees
   */
  static supportRequestShowAssigneesSearchDialog  = createAction(SupportActionTypes.SUPPORT_REQUEST_SHOW_ASSIGNEES_SEARCH);

  /**
   * Accept a support request.
   * @param supportId The support request ID.
   */
  static supportRequestAcceptAction = createAction(SupportActionTypes.SUPPORT_REQUEST_ACCEPT, (supportId: number) => ({
    supportId,
  }));

  /**
   * Notify that a support request has been accepted successfully.
   * @param supportId The support request ID.
   */
  static supportRequestAcceptSuccessAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_ACCEPT_SUCCESS,
    (supportId: number) => ({ supportId })
  );

  /**
   * Resolve a support request.
   * @param supportId The support request ID.
   */
  static supportRequestResolveAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_RESOLVE,
    (supportId: string) => ({ supportId })
  );

  /**
   * Notify that a support request has been resolved successfully.
   * @param supportId The support request ID.
   */
  static supportRequestResolveSuccessAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_RESOLVE_SUCCESS,
    (supportId: string) => ({ supportId })
  );

  /**
   * Assign a support request.
   * @param supportId The support request ID.
   * @param assignToPublicId The public ID of the user to assign the request to.
   */
  static supportRequestAssignAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_ASSIGN,
    (supportId: string, assignToPublicId: string) => ({ supportId, assignToPublicId })
  );

  /**
   * Notify that a support request has been assigned successfully.
   */
  static supportRequestAssignSuccessAction = createAction(SupportActionTypes.SUPPORT_REQUEST_ASSIGN_SUCCESS);

  /**
   * Notify that a support request has been assigned.
   * @param assignModel The assign model.
   */
  static supportRequestAssignReceivedAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_ASSIGN_RECEIVED,
    (assignModel: AssignSupportModel) => ({ assignModel })
  );

  /**
   * Notify that support requests assigned to user has loaded successfully
   * @param supportRequests Support requests assigned to currrent user
   */
  static loadAssignedSupportRequestSuccessAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_ASSIGNED_LOAD_SUCCESS,
    (supportRequests: SupportRequest[]) => ({supportRequests})
  )

  /**
   * Notify that user id has been loaded
   * @param userId of the current user
   */
  static loadCurrentUserIdAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_USER_ID_LOAD_SUCCESS,
    (userId: string) => ({userId})
  )

  /**
   * Notify that a support request will show as notifications
   * @param supportRequest of the support request to show
   */
  static upsertSupportRequestNotificationAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_SHOW_NOTIFICATION,
    (supportRequest: SupportRequest) => ({supportRequest})
  )

  /**
   * Notify that a support request will be dismissed from notifications
   * @param supportRequestId of the support request to dismiss
   */
  static dismissSupportRequestNotificationAction = createAction(
    SupportActionTypes.SUPPORT_REQUEST_DISMISS_NOTIFICATION,
    (supportRequestId: number) => ({supportRequestId})
  )

  /**
   * Notify that a support request notification should stop ringing
   * @param supportRequestId of the support request to stop ring
   */
    static stopRingingSupportRequestNotificationAction = createAction(
      SupportActionTypes.SUPPORT_REQUEST_STOP_RINGING,
      (supportRequestId: number) => ({supportRequestId})
    )

  /**
   * Notify that a support request load data filter is changed
   * @param filterOption of the support request to laod data
   */
    static supportRequestFilterChangeAction = createAction(
      SupportActionTypes.SUPPORT_REQUEST_FILTER_CHANGE,
      (filterOption: SupportRequestsFilterOptions) => ({filterOption})
    )
  
   /**
   * Notify that a chart togle is changed
   * @param showChart to show support request dashboard charts
   */
    static supportRequestChartToggleChangeAction = createAction(
      SupportActionTypes.SUPPORT_REQUEST_CHART_TOGGLE_CHANGE,
      (showChart: boolean) => ({showChart})
    )
}
